import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import ConfirmModal from '../components/ConfirmModal';
import styles from './TablePage.module.css';

type Notice = {
  id: string;
  title: string;
  content: string;
  is_pinned: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

const EMPTY = { title: '', content: '', is_pinned: false };

export default function NoticesPage() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Notice | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  const fetchNotices = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('notices')
      .select('*')
      .order('is_pinned', { ascending: false })
      .order('created_at', { ascending: false });
    setNotices(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotices(); }, [fetchNotices]);

  function openCreate() {
    setForm({ ...EMPTY });
    setCreating(true);
  }

  function openEdit(n: Notice) {
    setForm({ ...n });
    setCreating(false);
  }

  async function handleSave() {
    const { id, created_at, updated_at, ...rest } = form;
    const payload = { ...rest, updated_at: new Date().toISOString() };

    if (creating) {
      const { error } = await supabase.from('notices').insert([{
        ...payload,
        created_by: currentUser?.id ?? null,
      }]);
      if (error) { alert(error.message); return; }
    } else {
      const { error } = await supabase.from('notices').update(payload).eq('id', id);
      if (error) { alert(error.message); return; }
    }
    setForm(null);
    fetchNotices();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await supabase.from('notices').delete().eq('id', deleteTarget.id);
    setDeleteTarget(null);
    fetchNotices();
  }

  async function togglePin(n: Notice) {
    await supabase.from('notices').update({ is_pinned: !n.is_pinned }).eq('id', n.id);
    fetchNotices();
  }

  const pinned = notices.filter(n => n.is_pinned);
  const normal = notices.filter(n => !n.is_pinned);

  if (loading) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>📢 공지사항</h1>
          <span className={styles.count}>전체 {notices.length}개 · 핀 {pinned.length}개</span>
          <div className={styles.dbBadge}>🗄 notices</div>
        </div>
        <div className={styles.actions}>
          <button className="btn btn-ghost" onClick={fetchNotices}>↺</button>
          <button className="btn btn-primary" onClick={openCreate}>+ 공지 작성</button>
        </div>
      </div>

      {/* Pinned */}
      {pinned.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: 1.5, marginBottom: 10 }}>
            📌 고정 공지
          </div>
          {pinned.map(n => (
            <NoticeCard key={n.id} notice={n} expanded={expanded} setExpanded={setExpanded}
              onEdit={openEdit} onDelete={setDeleteTarget} onTogglePin={togglePin} pinned />
          ))}
        </div>
      )}

      {/* Normal */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: 1.5, marginBottom: 10 }}>
          일반 공지
        </div>
        {normal.length === 0 ? (
          <div className={styles.empty}><div className={styles.emptyIcon}>📭</div><div>공지사항이 없습니다</div></div>
        ) : (
          normal.map(n => (
            <NoticeCard key={n.id} notice={n} expanded={expanded} setExpanded={setExpanded}
              onEdit={openEdit} onDelete={setDeleteTarget} onTogglePin={togglePin} />
          ))
        )}
      </div>

      {/* Modal */}
      {form && (
        <div className="modal-overlay" onClick={() => setForm(null)}>
          <div className="modal-box" style={{ width: 580 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800 }}>{creating ? '공지 작성' : '공지 편집'}</h2>
              <button className="btn btn-ghost" style={{ padding: '4px 8px' }} onClick={() => setForm(null)}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>제목</label>
                <input className="input" value={form.title} onChange={e => setForm((p: any) => ({ ...p, title: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>내용</label>
                <textarea className="input" rows={6} style={{ resize: 'vertical' }}
                  value={form.content} onChange={e => setForm((p: any) => ({ ...p, content: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="pin" checked={form.is_pinned}
                  onChange={e => setForm((p: any) => ({ ...p, is_pinned: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <label htmlFor="pin" style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', cursor: 'pointer', fontWeight: 600 }}>
                  📌 상단 고정
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setForm(null)}>취소</button>
              <button className="btn btn-primary" onClick={handleSave}>{creating ? '등록' : '저장'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal title="공지 삭제" message={`"${deleteTarget.title}"을 삭제할까요?`}
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.6)',
  letterSpacing: 1, display: 'block', marginBottom: 6,
};

function NoticeCard({ notice, expanded, setExpanded, onEdit, onDelete, onTogglePin, pinned: isPinned }: {
  notice: Notice;
  expanded: string | null;
  setExpanded: (id: string | null) => void;
  onEdit: (n: Notice) => void;
  onDelete: (n: Notice) => void;
  onTogglePin: (n: Notice) => void;
  pinned?: boolean;
}) {
  const isExpanded = expanded === notice.id;

  return (
    <div style={{
      background: isPinned ? 'rgba(232,100,154,0.12)' : 'rgba(255,255,255,0.08)',
      border: `1px solid ${isPinned ? 'rgba(232,100,154,0.35)' : 'rgba(255,255,255,0.12)'}`,
      borderRadius: 16, marginBottom: 10, overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', cursor: 'pointer' }}
        onClick={() => setExpanded(isExpanded ? null : notice.id)}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {isPinned && <span style={{ fontSize: 12 }}>📌</span>}
            <span style={{ fontSize: 15, fontWeight: 700, color: 'white' }}>{notice.title}</span>
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
            {new Date(notice.created_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }}
            onClick={e => { e.stopPropagation(); onTogglePin(notice); }}>
            {notice.is_pinned ? '📌 해제' : '📌 고정'}
          </button>
          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }}
            onClick={e => { e.stopPropagation(); onEdit(notice); }}>편집</button>
          <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 11 }}
            onClick={e => { e.stopPropagation(); onDelete(notice); }}>삭제</button>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginLeft: 4 }}>{isExpanded ? '▲' : '▼'}</span>
        </div>
      </div>
      {isExpanded && (
        <div style={{ padding: '0 18px 18px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, marginTop: 14, whiteSpace: 'pre-wrap' }}>
            {notice.content}
          </p>
        </div>
      )}
    </div>
  );
}
